import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Eraser, Pen, Trash2, Undo2, Download, FilePlus, ChevronLeft, ChevronRight, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const COLORS = [
  { name: 'Black', value: '#1f2937' },
  { name: 'Red', value: '#ea4335' },
  { name: 'Blue', value: '#1a73e8' },
  { name: 'Green', value: '#34a853' },
  { name: 'Orange', value: '#f59e0b' },
  { name: 'Purple', value: '#7c3aed' },
  { name: 'White', value: '#ffffff' },
];

const PEN_SIZES = [2, 4, 6, 10, 16];

export const WhiteboardPage = () => {
  const { classId } = useParams();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState('#1f2937');
  const [penSize, setPenSize] = useState(4);
  const [tool, setTool] = useState('pen');
  const [classInfo, setClassInfo] = useState(null);

  // Multi-page state
  const [pages, setPages] = useState(['']); // Array of data URLs
  const [currentPage, setCurrentPage] = useState(0);
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState([-1]);

  useEffect(() => {
    const fetchClass = async () => {
      try {
        const r = await fetch(`${API}/classes`, { credentials: 'include' });
        if (r.ok) {
          const classes = await r.json();
          setClassInfo(classes.find(c => c.class_id === classId));
        }
      } catch (e) { /* ignore */ }
    };
    fetchClass();
  }, [classId]);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    tempCtx.drawImage(canvas, 0, 0);

    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(tempCanvas, 0, 0);
  }, []);

  useEffect(() => {
    resizeCanvas();
    saveToHistory();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  // Load page content when switching
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pageData = pages[currentPage];
    if (pageData) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = pageData;
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [currentPage]);

  const saveCurrentPage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = canvas.toDataURL();
    setPages(prev => {
      const copy = [...prev];
      copy[currentPage] = data;
      return copy;
    });
  };

  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = penSize * 4;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
      ctx.lineWidth = penSize;
    }
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDraw = (e) => {
    if (!isDrawing) return;
    e?.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.closePath();
    ctx.globalCompositeOperation = 'source-over';
    setIsDrawing(false);
    saveToHistory();
    saveCurrentPage();
  };

  const saveToHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = canvas.toDataURL();
    setHistory(prev => {
      const copy = [...prev];
      if (!copy[currentPage]) copy[currentPage] = [];
      const pageHist = copy[currentPage].slice(0, (historyIndex[currentPage] ?? -1) + 1);
      pageHist.push(data);
      if (pageHist.length > 30) pageHist.shift();
      copy[currentPage] = pageHist;
      return copy;
    });
    setHistoryIndex(prev => {
      const copy = [...prev];
      copy[currentPage] = Math.min((copy[currentPage] ?? -1) + 1, 29);
      return copy;
    });
  };

  const handleUndo = () => {
    const idx = historyIndex[currentPage] ?? 0;
    if (idx <= 0) return;
    const newIndex = idx - 1;
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      saveCurrentPage();
    };
    img.src = history[currentPage][newIndex];
    setHistoryIndex(prev => {
      const copy = [...prev];
      copy[currentPage] = newIndex;
      return copy;
    });
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    saveToHistory();
    saveCurrentPage();
    toast.success('Page cleared');
  };

  const handleAddPage = () => {
    saveCurrentPage();
    const newIdx = pages.length;
    setPages(prev => [...prev, '']);
    setHistory(prev => [...prev, []]);
    setHistoryIndex(prev => [...prev, -1]);
    setCurrentPage(newIdx);
    // Clear canvas for new page
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      saveToHistory();
    }, 50);
    toast.success(`Page ${newIdx + 1} added`);
  };

  const switchPage = (idx) => {
    if (idx < 0 || idx >= pages.length || idx === currentPage) return;
    saveCurrentPage();
    setCurrentPage(idx);
  };

  const handleDownloadPNG = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `whiteboard-${classInfo?.name || 'board'}-p${currentPage + 1}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Page saved as image');
  };

  const handleExportPDF = async () => {
    saveCurrentPage();
    toast.info('Generating PDF...');
    try {
      const { default: html2pdf } = await import('html2pdf.js');
      const container = document.createElement('div');
      container.style.width = '800px';

      // Use the latest pages state
      const latestPages = [...pages];
      const canvas = canvasRef.current;
      if (canvas) latestPages[currentPage] = canvas.toDataURL();

      for (let i = 0; i < latestPages.length; i++) {
        const pageDiv = document.createElement('div');
        pageDiv.style.width = '100%';
        pageDiv.style.marginBottom = i < latestPages.length - 1 ? '0' : '0';
        pageDiv.style.pageBreakAfter = 'always';
        if (latestPages[i]) {
          const img = document.createElement('img');
          img.src = latestPages[i];
          img.style.width = '100%';
          img.style.display = 'block';
          pageDiv.appendChild(img);
        } else {
          pageDiv.style.height = '600px';
          pageDiv.style.backgroundColor = '#ffffff';
        }
        container.appendChild(pageDiv);
      }

      document.body.appendChild(container);
      await html2pdf().set({
        margin: 0,
        filename: `whiteboard-${classInfo?.name || 'notes'}-${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'px', format: [800, 600], orientation: 'landscape' },
        pagebreak: { mode: ['css'] },
      }).from(container).save();
      document.body.removeChild(container);
      toast.success('PDF exported');
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('Failed to export PDF');
    }
  };

  const breadcrumb = [
    { label: 'Dashboard', path: '/app/dashboard' },
    { label: classInfo?.name || 'Class' },
    { label: 'Whiteboard' }
  ];

  return (
    <Layout breadcrumb={breadcrumb}>
      <div className="flex flex-col h-[calc(100vh-120px)]" data-testid="whiteboard-page">
        {/* Toolbar */}
        <div className="bg-white border border-google-grey-200 rounded-xl p-3 mb-3 flex items-center gap-3 flex-wrap">
          <div className="flex bg-google-grey-100 rounded-lg p-1">
            <Button variant="ghost" size="sm" className={`h-9 px-3 rounded-md ${tool === 'pen' ? 'bg-white shadow-sm text-google-blue' : 'text-google-grey-600'}`} onClick={() => setTool('pen')} data-testid="pen-tool-btn">
              <Pen className="w-4 h-4 mr-1.5" />Pen
            </Button>
            <Button variant="ghost" size="sm" className={`h-9 px-3 rounded-md ${tool === 'eraser' ? 'bg-white shadow-sm text-google-blue' : 'text-google-grey-600'}`} onClick={() => setTool('eraser')} data-testid="eraser-tool-btn">
              <Eraser className="w-4 h-4 mr-1.5" />Eraser
            </Button>
          </div>

          <div className="w-px h-8 bg-google-grey-200" />

          <div className="flex items-center gap-1.5">
            {COLORS.map((c) => (
              <button key={c.value} className={`w-7 h-7 rounded-full border-2 transition-all ${color === c.value && tool === 'pen' ? 'border-google-blue scale-110 ring-2 ring-blue-200' : 'border-google-grey-300'} ${c.value === '#ffffff' ? 'bg-white' : ''}`} style={{ backgroundColor: c.value }} onClick={() => { setColor(c.value); setTool('pen'); }} title={c.name} data-testid={`color-${c.name.toLowerCase()}`} />
            ))}
          </div>

          <div className="w-px h-8 bg-google-grey-200" />

          <div className="flex items-center gap-1.5">
            {PEN_SIZES.map((size) => (
              <button key={size} className={`flex items-center justify-center w-8 h-8 rounded-lg border transition-all ${penSize === size ? 'border-google-blue bg-blue-50' : 'border-google-grey-200 hover:bg-google-grey-50'}`} onClick={() => setPenSize(size)} title={`Size ${size}`} data-testid={`pen-size-${size}`}>
                <div className="rounded-full bg-google-grey-800" style={{ width: Math.min(size, 14), height: Math.min(size, 14) }} />
              </button>
            ))}
          </div>

          <div className="flex-1" />

          {/* Page controls */}
          <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-2 py-1">
            <Button variant="ghost" size="sm" onClick={() => switchPage(currentPage - 1)} disabled={currentPage === 0} className="h-7 w-7 p-0" data-testid="prev-page-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs font-medium text-slate-600 min-w-[60px] text-center" data-testid="page-indicator">
              Page {currentPage + 1}/{pages.length}
            </span>
            <Button variant="ghost" size="sm" onClick={() => switchPage(currentPage + 1)} disabled={currentPage === pages.length - 1} className="h-7 w-7 p-0" data-testid="next-page-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleAddPage} className="h-7 px-2 text-teal-600 hover:text-teal-700" data-testid="add-page-btn">
              <FilePlus className="w-4 h-4" />
            </Button>
          </div>

          <div className="w-px h-8 bg-google-grey-200" />

          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={handleUndo} disabled={(historyIndex[currentPage] ?? 0) <= 0} className="border-google-grey-300 text-google-grey-600 h-9" data-testid="undo-btn">
              <Undo2 className="w-4 h-4 mr-1.5" />Undo
            </Button>
            <Button variant="outline" size="sm" onClick={handleClear} className="border-google-grey-300 text-google-grey-600 h-9" data-testid="clear-btn">
              <Trash2 className="w-4 h-4 mr-1.5" />Clear
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPNG} className="border-google-grey-300 text-google-grey-600 h-9" data-testid="download-btn">
              <Download className="w-4 h-4 mr-1.5" />PNG
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF} className="border-google-grey-300 text-google-grey-600 h-9" data-testid="export-pdf-btn">
              <FileDown className="w-4 h-4 mr-1.5" />PDF
            </Button>
          </div>
        </div>

        {/* Canvas */}
        <div ref={containerRef} className="flex-1 bg-white rounded-xl border border-google-grey-200 overflow-hidden cursor-crosshair">
          <canvas ref={canvasRef} className="w-full h-full touch-none" onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw} onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} data-testid="whiteboard-canvas" />
        </div>
      </div>
    </Layout>
  );
};
